import React from "react";
import {
  Actions,
  Badge,
  CountCell,
  DateCell,
  NameCell,
  SubduedCell,
} from "@";
import type { ColumnDef } from "@tanstack/react-table";
import type { WhatsappCampaign } from "../types";
import { CampaignStatus } from "../types";

interface TableActionsProps {
  row: WhatsappCampaign;
}

export const TableActions: React.FC<TableActionsProps> = ({ row }) => {
  return <Actions row={row} url="whatsapp-campaign" notEdit notDelete />;
};

interface StatusInfo {
  title: string;
  color: string;
}

const statusMap: Record<CampaignStatus, StatusInfo> = {
  [CampaignStatus.Draft]: { title: "مسودة", color: "default" },
  [CampaignStatus.Processing]: { title: "قيد المعالجة", color: "yellow" },
  [CampaignStatus.Completed]: { title: "مكتمل", color: "green" },
  [CampaignStatus.Failed]: { title: "فشل", color: "red" },
};

// Truncating description cell — full text in the title attr on hover so
// the row stays one line tall but the user can still get the full content.
const DescriptionCell = ({ value }: { value?: string | null }) => {
  if (!value) {
    return <SubduedCell value={value} />;
  }
  const truncated = value.length > 60 ? value.slice(0, 60) + "…" : value;
  return (
    <span
      title={value}
      className="text-sm text-gray-600 dark:text-gray-400"
    >
      {truncated}
    </span>
  );
};

// Truncating name cell with full text on hover.
const TruncatedNameCell = ({ value }: { value?: string | null }) => {
  if (!value) {
    return <NameCell name={value} />;
  }
  const truncated = value.length > 50 ? value.slice(0, 50) + "…" : value;
  return (
    <span
      title={value}
      className="font-semibold tracking-tight text-gray-900 dark:text-gray-100"
    >
      {truncated}
    </span>
  );
};

// TanStack table columns format
export const tableColumns: ColumnDef<WhatsappCampaign>[] = [
  {
    id: "name",
    header: "الاسم",
    size: 220,
    cell: ({ row }) => <TruncatedNameCell value={row.original.name} />,
  },
  {
    id: "description",
    header: "الوصف",
    size: 350,
    cell: ({ row }) => <DescriptionCell value={row.original.description} />,
  },
  {
    id: "status",
    header: "الحالة",
    size: 130,
    cell: ({ row }) => {
      const status = row.original.status;
      const statusInfo = statusMap[status] || {
        title: "غير معروف",
        color: "gray",
      };
      return <Badge title={statusInfo.title} color={statusInfo.color} />;
    },
  },
  {
    id: "totalRecipientsCount",
    header: "عدد المستلمين",
    size: 130,
    cell: ({ row }) => (
      <CountCell value={row.original.totalRecipientsCount} label="مستلم" />
    ),
  },
  {
    id: "sentCount",
    header: "تم الإرسال",
    size: 110,
    cell: ({ row }) => <CountCell value={row.original.sentCount} />,
  },
  {
    id: "failedCount",
    header: "فشل الإرسال",
    size: 110,
    cell: ({ row }) => <CountCell value={row.original.failedCount} />,
  },
  {
    id: "pendingCount",
    header: "في الانتظار",
    size: 110,
    cell: ({ row }) => <CountCell value={row.original.pendingCount} />,
  },
  {
    id: "createdOn",
    header: "تاريخ الإنشاء",
    size: 130,
    cell: ({ row }) => <DateCell value={row.original.createdOn} />,
  },
  {
    id: "completedAt",
    header: "تاريخ الانتهاء",
    size: 130,
    cell: ({ row }) => <DateCell value={row.original.completedAt} />,
  },
];
